import { Row, Col, message, Button, Table, Pagination, Switch, Popconfirm, Tag, Tooltip } from "antd";
import { ColumnsType } from "antd/lib/table";
import { IVendor } from "common/interface/IVendor";
import { AppPageHeader } from "components/AppPageHeader";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useDocumentTitle } from "hooks/usePageTitle";
import { useCallback, useEffect, useState } from "react";
import { BsPlus, BsTelephone, BsWhatsapp } from "react-icons/bs";
import { MdOutlineEmail, MdHistory } from "react-icons/md";
import { FiEdit2 } from "react-icons/fi";
import { getAllVendors, getVendorAmendments, submitVendorAmendment } from "store/vendor/vendorSlice";
import { AmendmentHistoryModal } from "components/AmendmentHistoryModal/AmendmentHistoryModal";
import { routes } from "common/routes";
import styled from "styled-components";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";
import { AppActionStrip } from "components/AppActionStrip/AppActionStrip";
import {
  AppFilter,
  AppFilterButton,
  AppVendorFilter,
} from "components/AppFilter";

type PartialVendorType = Partial<IVendor> & { key: string };
const MESSAGE_KEY = "all_vendors";

const headerKeys: { [key: string]: string } = {
  id: "Vendor Code",
  extVendorId: "Ext Vendor ID",
  name: "Name",
  pocName: "POC Name",
  address: "Address",
  contactNumber: "Contact Number",
  supplyLocation: "Supply Location",
  status: "Status",
  actions: "Actions",
};

const StyledContactItem = styled.div`
  display: flex;
  align-items: center;

  & > a {
    margin-left: 1rem;
  }
`;

const StyledPagination = styled(Pagination)`
  display: flex;
  justify-content: flex-end;
  margin-top: 1rem;
`;

const StyledActionLink = styled(NavLink)`
  border: 1px solid;
  padding: 0.2rem;
  border-radius: 50%;
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StyledActionButton = styled.button`
  border: 1px solid;
  padding: 0.2rem;
  border-radius: 50%;
  width: 1.5rem;
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  cursor: pointer;
  color: inherit;
`;

const disabledActionStyle: React.CSSProperties = {
  opacity: 0.4,
  cursor: "not-allowed",
  border: "1px solid",
  padding: "0.2rem",
  borderRadius: "50%",
  width: "1.5rem",
  height: "1.5rem",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const VendorHomePage = () => {
  useDocumentTitle("All Vendors");
  const dispatch = useAppDispatch();
  const [columns, setColumns] = useState<ColumnsType<PartialVendorType>>();
  const [rows, setRows] = useState<PartialVendorType[]>();
  const { vendors, status, error, amendments, amendmentStatus } = useAppSelector((state) => state.vendors);
  const [filterState, setFilterState] = useState(false);
  const [historyModal, setHistoryModal] = useState<{ id: string; name: string } | null>(null);
  const [searchParams] = useSearchParams();
  const [filterCount, setFilterCount] = useState(0);
  const navigate = useNavigate();

  const getVendorData = useCallback(
    (pageNum = 1, pageSize = 25, filter = "") => {
      dispatch(getAllVendors({ pageNo: pageNum, pageSize, filter }));
    },
    [dispatch]
  );

  useEffect(() => {
    const filter = searchParams.toString();
    setFilterCount(
      Object.keys(Object.fromEntries(new URLSearchParams(filter))).length
    );
    getVendorData(1, 25, filter);
  }, [dispatch, getVendorData, searchParams]);

  useEffect(() => {
    if (status === "loading") {
      message.loading({ content: "Loading Vendors", key: MESSAGE_KEY });
    } else if (status === "success") {
      message.success({
        content: "Vendors loaded!",
        key: MESSAGE_KEY,
      });
    } else if (status === "error") {
      message.error({ content: error, key: MESSAGE_KEY });
    }

    return () => {
      message.destroy();
    };
  }, [status, error]);

  useEffect(() => {
    const vendorData = vendors && vendors.data ? vendors.data : [];
    const data: PartialVendorType[] = vendorData.map((vendor, i) => {
      const {
        id,
        vendorCode,
        extVendorId,
        address,
        name,
        pocName,
        email,
        primaryPhone,
        secondaryPhone,
        whatsappPhone,
        location,
        status,
        pendingAmendmentId,
      } = vendor;

      const newRow = {
        id,
        vendorCode,
        address,
        name,
        pocName,
        email,
        primaryPhone,
        secondaryPhone,
        whatsappPhone,
        extVendorId,
        location,
        status,
        pendingAmendmentId,
      };
      return { ...newRow, key: `req-${i}`, ellipses: true };
    });

    const tableHeaders: ColumnsType<PartialVendorType> = [
      ...Object.keys(headerKeys).map((key) => ({
        key,
        title: headerKeys[key],
        dataIndex: key,
        render: (text: string, record: PartialVendorType) => {
          if (key === "id") {
            if (record.pendingAmendmentId) {
              return <>{record.vendorCode ?? record.id}</>;
            }
            return <Button type="link" onClick={() => navigate(`/vendors/edit/${record.id}`)}>{record.vendorCode ?? record.id}</Button>;
          }
          if (key === "address") {
            return (
              <>
                <div>{record.address?.address1}</div>
                <div>{record.address?.address2}</div>
                <div>
                  {record.address?.city}, {record.address?.state}
                </div>
                <div>{record.address?.pincode}</div>
              </>
            );
          }

          if (key === "supplyLocation") {
            return (
              <>
                <div>{record.location}</div>
              </>
            );
          }

          if (key === "contactNumber") {
            return (
              <>
                <StyledContactItem>
                  <BsTelephone />
                  <a href={`tel:${record.primaryPhone}`}>
                    {record.primaryPhone}
                  </a>
                </StyledContactItem>
                {record.secondaryPhone && (
                  <StyledContactItem>
                    <BsTelephone />
                    <a href={`tel:${record.secondaryPhone}`}>
                      {record.secondaryPhone}
                    </a>
                  </StyledContactItem>
                )}
                {record.whatsappPhone && (
                  <StyledContactItem>
                    <BsWhatsapp />
                    <a
                      href={`https://wa.me/${record.whatsappPhone}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {record.whatsappPhone}
                    </a>
                  </StyledContactItem>
                )}
              </>
            );
          }

          // if (key === "email") {
          //   return (
          //     <StyledContactItem>
          //       <MdOutlineEmail />
          //       <a href={`mailto: ${record.email}`}>{record.email}</a>
          //     </StyledContactItem>
          //   );
          // }

          if (key === "status") {
            const hasPendingAmendment = !!record.pendingAmendmentId;
            const nextStatus = record.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
            return (
              <>
                {hasPendingAmendment && (
                  <Tag color="gold" style={{ marginBottom: 4 }}>
                    Pending #{record.pendingAmendmentId}
                  </Tag>
                )}
                <Popconfirm
                  title={`Submit a request to mark this vendor as ${nextStatus}? It will apply once approved.`}
                  okText="Submit Request"
                  cancelText="Cancel"
                  onConfirm={() => onStatusToggle(nextStatus, record.id!)}
                  disabled={hasPendingAmendment}
                >
                  <Switch checked={record.status === "ACTIVE"} disabled={hasPendingAmendment} />
                </Popconfirm>
              </>
            );
          }

          if (key === "actions") {
            const hasPendingAmendment = !!record.pendingAmendmentId;
            return (
              <Row
                align="middle"
                gutter={8}
                style={{
                  display: "flex",
                  flexWrap: "nowrap",
                }}
              >
                <Col>
                  {hasPendingAmendment ? (
                    <Tooltip title={`Pending request #${record.pendingAmendmentId}`}>
                      <span style={disabledActionStyle}>
                        <FiEdit2 />
                      </span>
                    </Tooltip>
                  ) : (
                    <StyledActionLink to={`/vendors/edit/${record.id}`}>
                      <FiEdit2 />
                    </StyledActionLink>
                  )}
                </Col>
                <Col>
                  <Tooltip title="View amendment history">
                    <StyledActionButton
                      type="button"
                      onClick={() => setHistoryModal({ id: String(record.id), name: record.name ?? "" })}
                    >
                      <MdHistory />
                    </StyledActionButton>
                  </Tooltip>
                </Col>
              </Row>
            );
          }

          return <>{text}</>;
        },
      })),
    ];

    setColumns(tableHeaders);
    setRows(data);
  }, [vendors]);

  const redirectToNewVendorPage = () => {
    navigate("new");
  };

  const onStatusToggle = useCallback((nextStatus: "ACTIVE" | "INACTIVE", vendorId: number) => {
    dispatch(
      submitVendorAmendment({
        entityType: "VENDOR",
        entityId: String(vendorId),
        requestType: "TOGGLE_STATUS",
        payload: { status: nextStatus },
      })
    )
      .unwrap()
      .then(() => {
        message.success({
          content: "Status change request submitted for approval!",
          key: MESSAGE_KEY,
        });
      })
      .catch((e: any) => {
        message.error({ content: e?.message ?? "Something went wrong!", key: MESSAGE_KEY });
      });
  }, [dispatch]);

  const fetchHistoryPage = useCallback(
    (pageNo: number, pageSize: number) => {
      if (!historyModal) return;
      dispatch(getVendorAmendments({ pageNo, pageSize, entityId: historyModal.id }));
    },
    [dispatch, historyModal]
  );

  const Paginator = () => {
    if (vendors) {
      const { pageNo, pageSize, total } = vendors;
      return (
        <StyledPagination
          current={pageNo}
          pageSize={pageSize}
          total={total}
          onChange={(page, size) => {
            const filter = searchParams.toString();
            getVendorData(page, size, filter);
          }}
        />
      );
    }

    return <></>;
  };

  return (
    <>
      <Row>
        <Col>
          <AppPageHeader title="All Vendors" />
        </Col>
      </Row>
      <Row>
        <AppActionStrip>
          <div className="action-strip__item">
            <AppFilterButton
              active={filterState}
              onClick={() => setFilterState(!filterState)}
              filterCount={filterCount}
            />
          </div>
          <Button
            onClick={redirectToNewVendorPage}
            type="primary"
            className="action-strip__item"
            icon={<BsPlus />}
          >
            New Vendor
          </Button>
        </AppActionStrip>
      </Row>
      <Row style={{ marginBottom: "1rem" }}>
        <AppFilter expanded={filterState}>
          <AppVendorFilter />
        </AppFilter>
      </Row>
      {rows && rows.length ? (
        <Row>
          <Col xs={24}>
            <Table
              columns={columns}
              dataSource={rows}
              scroll={{ x: "100vh", y: "65vh" }}
              size="small"
              pagination={false}
              loading={status === "loading"}
            />
          </Col>
          <Col xs={24}>
            <Paginator />
          </Col>
        </Row>
      ) : (
        status !== "loading" && <>No Vendors to show.</>
      )}
      <AmendmentHistoryModal
        title={`Amendment History${historyModal?.name ? ` — ${historyModal.name}` : ""} (#${historyModal?.id})`}
        open={!!historyModal}
        onClose={() => setHistoryModal(null)}
        amendments={amendments}
        loading={amendmentStatus === "loading"}
        fetchPage={fetchHistoryPage}
        detailPath={(amendmentId) =>
          `/${routes.VENDOR.AMENDMENT_DETAIL.replace(":id", String(amendmentId))}`
        }
      />
    </>
  );
};

export default VendorHomePage;
